import { Injectable, Injector } from '@angular/core';
import {
    HttpInterceptor,
    HttpRequest,
    HttpHandler,
    HttpEvent,
    HttpResponse
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap, tap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { SharedService } from '../../shared/services/shared.service';
import { GuestTokenService } from '../services/guest-token.service';
import { environment } from '../../../environments/environment';
import { CorrelationIdService } from '../services/correlation-id.service';

@Injectable({
    providedIn: 'root'
})
export class AuthInterceptor implements HttpInterceptor {

    // This method is used to initialize the class instance using injector and router
    constructor(
        private injector: Injector,
        private router: Router
    ) { }

    // This method is used to intercept using req and next
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        const correlationId = this.injector.get(CorrelationIdService).createCorrelationId();
        req = req.clone({
            setHeaders: {
                'X-DCCI-CorrelationID': correlationId
            }
        });

        const authenticationGatewayHeaders = this.getAuthenticationGatewayHeaders(req.url);
        const oauthBypassEndpoints = [
            '/DCAuthToken/AuthTokenREST/authToken',
            '/DCAuthToken/AuthTokenREST/renewToken',
            '/authentication/v1/oauth2/token',
            '/DCAuthToken/InitiateUAEPassAuthREST/initiateUAEPassAuth',
            '/DCAuthToken/InitiateUAEPassAuthREST/completeUAEPassAuth',
            '/DCAuthToken/LinkUAEPassUserREST/linkUAEPassUser',
            '/DCAuthToken/Recovery/RecoverUsername',
            '/DCAuthToken/Recovery/RecoverPassword',
            '/oauth2/token', '/.well-known/openid-configuration', '/userinfo'
        ];
        const isOauthTokenRequest = req.url.includes('/oauth2/token');
        //Special check for send soruce cp to get liferay token in response
        if (isOauthTokenRequest) {
            const tokenRequest = req.clone({
                setHeaders: {
                    ...authenticationGatewayHeaders,
                    Source: 'CP'
                }
            });
            return this.handleRequest(next, tokenRequest, correlationId);
        }

        //const oauthBypassEndpoints = ['/oauth2/token', '/.well-known/openid-configuration', '/userinfo'];
        const shouldBypassAuth = oauthBypassEndpoints.some((endpoint) => req.url.includes(endpoint));
        if (shouldBypassAuth) {
            const bypassRequest = Object.keys(authenticationGatewayHeaders).length
                ? req.clone({ setHeaders: authenticationGatewayHeaders })
                : req;
            return this.handleRequest(next, bypassRequest, correlationId);
        }

        const shareService = this.injector.get(SharedService);
        const authService = this.injector.get(AuthService);
        const guestTokenService = this.injector.get(GuestTokenService);
        const normalizedUrl = req.url.toLowerCase();
        const isVenueBookingRequest = normalizedUrl.includes('/venue/');
        const loginUser: any = shareService.getUser();
        const hasAuthorizationHeader = req.headers.has('Authorization');
        const venueServiceToken = isVenueBookingRequest ? this.getVenueServiceToken() : null;
        const accessToken = loginUser ? authService.getAccessToken() : null;
        const guestToken = venueServiceToken || (loginUser ? null : this.getGuestAccessToken());
        let updatedReq = req;

        const requiresGuestEndpoint = !loginUser && (
            req.url.includes('DCCICPIntegration/RunSiebelProcessRest/RunSiebelProcess') ||
            req.url.includes('DCCICPIntegration/ListOfValues_REST/GetListOfValues') ||
            req.url.includes('DCCaseIntegrationAPI/CaseIntegrationAPI_RESTProxy') ||
            req.url.includes('DCCICPIntegration/GenerateEmailOTP_REST') ||
            req.url.includes('DCCICPIntegration/VerifyEmailOTP_REST')
        );
        //Add guest in url for the DCCICPIntegration API when unauthenticated
        if (requiresGuestEndpoint) {
            const modifiedUrl = req.url.replace('/v1/', '/v1/guest/');
            updatedReq = req.clone({ url: modifiedUrl });
        }

        const buildHeaders = (token: string | null) => {
            const resolvedAuthorization = hasAuthorizationHeader
                ? req.headers.get('Authorization') || ''
                : token
                    ? `Bearer ${token}`
                    : '';

            const headersConfig: { [key: string]: string } = {
                ...authenticationGatewayHeaders,
                "Authorization": resolvedAuthorization,
                "lang": shareService.getCurrentLang() == "ar" ? "ARA" : "ENU",
                "Source": "CP"
            };
            if (normalizedUrl.includes('venue-reservation-rest/v1.0/room-book')) {
                headersConfig['Accept-Language'] = shareService.getCurrentLang() === 'ar' ? 'ar-SA' : 'en-US';
            }
            if (updatedReq.url.includes('scim2/Me')) {
                headersConfig['Accept'] = 'application/scim+json';
                headersConfig['Content-Type'] = 'application/scim+json';
            }
            return updatedReq.clone({ setHeaders: headersConfig });
        };

        const sendRequest = (token: string | null) =>
            this.handleRequest(next, buildHeaders(token), correlationId);

        if (!loginUser && requiresGuestEndpoint && !guestToken && !isVenueBookingRequest) {
            return guestTokenService.ensureGuestToken().pipe(
                switchMap((token) => sendRequest(token)),
                catchError(() => sendRequest(null))
            );
        }

        if (loginUser && !accessToken && authService.getRefreshToken()) {
            return authService.refreshToken().pipe(
                switchMap(() => sendRequest(authService.getAccessToken())),
                catchError(() => sendRequest(null))
            );
        }

        const activeToken = venueServiceToken || accessToken || guestToken;
        return sendRequest(activeToken);
    }

    private handleRequest(
        next: HttpHandler,
        request: HttpRequest<any>,
        correlationId: string
    ): Observable<HttpEvent<any>> {
        const correlationIdService = this.injector.get(CorrelationIdService);
        return next.handle(request).pipe(
            tap(event => {
                if (event instanceof HttpResponse) {
                    const responseCorrelationId = event.headers.get('X-DCCI-CorrelationID');
                    const requestCorrelationId = request.headers.get('X-DCCI-CorrelationID');
                    const resolvedCorrelationId = responseCorrelationId || requestCorrelationId || correlationId;
                    correlationIdService.setCurrentCorrelationId(resolvedCorrelationId);
                    if (this.hasBusinessError(event.body)) {
                        correlationIdService.setTechnicalErrorCorrelationId(resolvedCorrelationId);
                    }
                }
            }),
            catchError(err => {
                const requestCorrelationId = request.headers.get('X-DCCI-CorrelationID');
                const resolvedCorrelationId = requestCorrelationId || correlationId;
                correlationIdService.setCurrentCorrelationId(resolvedCorrelationId);
                correlationIdService.setTechnicalErrorCorrelationId(resolvedCorrelationId);
                if (err.status === 401) {
                    this.router.navigate(['/unauthorized']);
                }
                // if ([400, 404, 405, 500].includes(err.status)) {
                // }
                return throwError(() => err);
            })
        );
    }

    private hasBusinessError(body: unknown): boolean {
        if (!body || typeof body !== 'object') {
            return false;
        }

        const response = body as Record<string, unknown>;
        const errorCode = String(response['Error Code'] ?? response['errorCode'] ?? '').trim();
        const errorMessage = String(response['Error Message'] ?? response['errorMessage'] ?? '').trim();
        if (errorCode || errorMessage || response['success'] === false) {
            return true;
        }

        const status = typeof response['status'] === 'string'
            ? response['status'].trim().toUpperCase()
            : '';
        return ['ERROR', 'FAILED', 'FAILURE', 'BAD_REQUEST'].includes(status);
    }

    private getAuthenticationGatewayHeaders(url: string): { [key: string]: string } {
        const clientGatewayKey = String(environment.clientGatewayKey ?? '').trim();
        if (!clientGatewayKey || !url.toLowerCase().includes('/authentication')) {
            return {};
        }

        return {
            'X-Client-Gateway-Key': clientGatewayKey
        };
    }

    private getGuestAccessToken(): string | null {
        try {
            const raw = localStorage.getItem('guest_access_token');
            if (!raw) {
                return null;
            }
            const parsed = JSON.parse(raw) as { access_token?: string; expiresAt?: number };
            if (parsed?.access_token && parsed?.expiresAt && parsed.expiresAt > Date.now()) {
                return parsed.access_token;
            }
        } catch {
            return null;
        }
        return null;
    }

    private getVenueServiceToken(): string | null {
        const token = String(localStorage.getItem('venue_service_token') ?? '').trim();
        if (!token) {
            return null;
        }

        const payload = this.decodeJwtPayload(token);
        const exp = Number(payload?.['exp'] ?? 0);
        const bufferMs = 60_000;
        if (!exp || exp * 1000 - bufferMs <= Date.now()) {
            localStorage.removeItem('venue_service_token');
            return null;
        }

        return token;
    }

    private decodeJwtPayload(token: string): Record<string, any> | null {
        const parts = String(token ?? '').split('.');
        if (parts.length < 2) {
            return null;
        }

        try {
            const normalized = parts[1]
                .replace(/-/g, '+')
                .replace(/_/g, '/');
            const padded = normalized.padEnd(normalized.length + ((4 - normalized.length % 4) % 4), '=');
            return JSON.parse(window.atob(padded));
        } catch {
            return null;
        }
    }
}
